/**
 * The share card the FIRST HTML response already told the crawler about.
 *
 * The share dialogs used to compose their own 1200×630 image in the browser from the record's SVG.
 * On the demo — a page with no server — that is the only thing they can do, and it is right. In the
 * product it was wrong twice over: the visitor was shown a picture that is not the one LinkedIn
 * fetches, and the composition could fail (a font, a `<foreignObject>`, a tainted canvas) with the
 * failure swallowed by `.catch(() => undefined)`, leaving a broken image frame and no explanation.
 *
 * So on a server-rendered page the dialogs now show the server's own card. The address is not
 * rebuilt here from an origin and a code — that would be a second copy of a rule, free to drift
 * from the first. It is READ OUT OF THE DOCUMENT's own `og:image`, which `frontend/server.mjs`
 * wrote into the first response. Whatever the crawler was given is, by construction, what the
 * dialog shows. `og:title` is read the same way, so the dialog's heading cannot say something the
 * link card does not.
 *
 * On the demo there are no such tags, `serverShareCard()` answers null, and the browser-side
 * composition stays exactly as it was.
 *
 * WHY THE IMAGE IS FETCHED RATHER THAN POINTED AT
 * -----------------------------------------------
 * The deployment's Content-Security-Policy is `img-src 'self' data: blob:` — deliberately narrow,
 * and not widened for this. The API is a different origin, so `<img src="https://api…/card.png">`
 * would be blocked with no visible error at all. `connect-src` already names that API origin
 * (it is where the page reads the record from), so the bytes are fetched and handed to the `<img>`
 * as a `blob:` URL, which the policy does permit. The picture is the server's; only the transport
 * changed.
 */
import { useCallback, useEffect, useState } from 'react';
import { memberPostApiOrigin } from './linkedin-member-post';

export interface ServerShareCard {
  /** Absolute URL of the image the page's `og:image` names. */
  imageUrl: string;
  /** The page's `og:title`, so a dialog heading cannot disagree with the link card. */
  title: string | null;
}

function metaContent(selector: string): string | null {
  if (typeof document === 'undefined') return null;
  const el = document.head?.querySelector(selector);
  const value = el?.getAttribute('content')?.trim();
  return value ? value : null;
}

/** The one place the card's address is written, so the derivation below cannot drift from the head. */
const cardPath = (publicCode: string): string =>
  `/api/v1/public/credentials/${encodeURIComponent(publicCode)}/social-card.png`;

/** True when this address is THIS record's card and not another record's, or an older page's. */
function namesCode(imageUrl: string, publicCode: string): boolean {
  try {
    return new URL(imageUrl).pathname === cardPath(publicCode);
  } catch {
    return false;
  }
}

/**
 * The card for the certificate on screen, or null when this deployment has no server to ask.
 *
 * TWO SOURCES, IN THIS ORDER, AND «NO HEAD» IS NOT «NO SERVER»
 * ------------------------------------------------------------
 * The head comes first: `og:image` is the address the server itself composed, so what the dialog
 * shows is by construction what the crawler was given.
 *
 * But the head is not the test for whether there IS a server. `frontend/server.mjs` time-boxes its
 * record read at 2.5 s and falls back to `GENERIC_TAGS`, which carry no `og:image` at all — so a
 * slow read, on a perfectly normal product page, used to make this answer null. Null means «demo»
 * to the caller, and the visitor was shown a picture composed in their own browser instead of the
 * one the post will actually carry. A timeout somewhere else decided which image a member saw.
 *
 * So when the head has no usable card, the address is derived from the two things a product
 * deployment always has: the API origin it is configured with, and the code of the record on
 * screen. That is the same fallback the connect flow already uses (`memberPostApiOrigin`), reused
 * rather than restated. Only a deployment with an EXPLICITLY configured API has one — the demo has
 * none, so the demo still answers null and keeps composing its own picture, exactly as before.
 *
 * The derived address carries no `?v=` design version. That query exists to keep a CRAWLER from
 * serving an old drawing from its cache; this fetch is `no-store` and asks the server every time,
 * so there is nothing to bust.
 *
 * A head naming a DIFFERENT record is refused rather than trusted: whatever is on screen is what
 * the dialog is about.
 */
export function serverShareCard(publicCode?: string | null): ServerShareCard | null {
  const code = publicCode?.trim() ? publicCode.trim() : null;
  const imageUrl = metaContent('meta[property="og:image"]');
  if (imageUrl) {
    /* Only an absolute http(s) address is usable; anything else is a malformed head, not a card. */
    let usable = false;
    try {
      const url = new URL(imageUrl);
      usable = url.protocol === 'https:' || url.protocol === 'http:';
    } catch {
      usable = false;
    }
    if (usable && (!code || namesCode(imageUrl, code)))
      return { imageUrl, title: metaContent('meta[property="og:title"]') };
  }

  if (!code) return null;
  const origin = memberPostApiOrigin();
  if (!origin) return null;
  /*
   * No title from the head here, deliberately. Reaching this line means the head is the generic
   * one, whose `og:title` is the product's name — and a dialog heading that says «HPW Credentials»
   * where the certificate's own title belongs is worse than no heading from the head at all, so the
   * caller falls back to the record's own words.
   */
  return { imageUrl: `${origin}${cardPath(code)}`, title: null };
}

/**
 * The picture a MEMBER POST will carry, derived from the address the server itself wrote.
 *
 * The two are deliberately different images. `og:image` is the automatic link card and stays that
 * way, because it is what every platform fetches when the link is pasted anywhere. A member post,
 * on the other hand, uploads the author's own arrangement when this certificate has one saved —
 * so a confirmation step that promises «the image your post will carry» and then shows the link
 * card is promising the wrong picture.
 *
 * Derived rather than rebuilt: the origin, the code and the shape of the path all come from the
 * document's own `og:image`, so there is no second copy of the address rule free to drift from
 * the first. Only the last segment is swapped, and only when it is the one this expects — a head
 * that names anything else yields null and the dialog keeps showing what it showed.
 *
 * The version query goes with it: the automatic card carries `?v=N` so a redesign is not served
 * from a crawler's cache, and this route answers `no-store` and has no version of its own.
 */
export function memberPostShareImage(
  card: ServerShareCard | null,
  /**
   * The language the member is sharing in, when it is not the record's own.
   *
   * `og:image` cannot carry it — a crawler fetching a pasted link has no tab open — so the link
   * card stays the record's own language and this ONE address, the picture the post will actually
   * upload, carries the choice. Absent means the record's own, which is what it has always meant.
   */
  lang?: 'tr' | 'en',
): string | null {
  if (!card) return null;
  try {
    const url = new URL(card.imageUrl);
    if (!url.pathname.endsWith('/social-card.png')) return null;
    url.pathname = url.pathname.replace(/\/social-card\.png$/, '/linkedin-share-image.png');
    url.search = lang ? `?lang=${lang}` : '';
    url.hash = '';
    return url.toString();
  } catch {
    return null;
  }
}

export type ShareCardStatus = 'loading' | 'ready' | 'error';

export interface ShareCardImage {
  status: ShareCardStatus;
  /** A `blob:` URL while ready; null otherwise. */
  src: string | null;
  /** Set when `status` is `error`. A sentence a person can act on, never a status code. */
  error: string | null;
  /** Re-runs the fetch. Wired to the dialog's «try again» control. */
  retry: () => void;
}

/** Anything that is not a picture is a failure, however successful the response looked. */
async function fetchCard(url: string, signal: AbortSignal): Promise<Blob> {
  const res = await fetch(url, { signal, credentials: 'omit', cache: 'no-store' });
  if (!res.ok) throw new Error(String(res.status));
  const blob = await res.blob();
  if (!blob.type.startsWith('image/')) throw new Error('not-an-image');
  return blob;
}

/**
 * Loads the server card, reporting every outcome.
 *
 * Nothing is swallowed: a network failure, a refusal and a non-image body all end in `error` with
 * a sentence and a working retry, because the alternative — the old `.catch(() => undefined)` —
 * showed the visitor a broken frame and told them nothing.
 */
export function useShareCardImage(url: string | null, message: string): ShareCardImage {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{ status: ShareCardStatus; src: string | null; error: string | null }>({
    status: url ? 'loading' : 'error',
    src: null,
    error: null,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is not read inside the effect; it IS the retry signal, and bumping it is what re-runs the fetch
  useEffect(() => {
    if (!url) return;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setState({ status: 'loading', src: null, error: null });
    fetchCard(url, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ status: 'ready', src: objectUrl, error: null });
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setState({ status: 'error', src: null, error: message });
      });
    return () => {
      controller.abort();
      /* The blob is held by the browser until it is released; a dialog opened repeatedly must not
         leak one image per open. */
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, attempt, message]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}
